import { normalizeShopifyShopId } from "#hydrogen/core/analytics/utils/shop";

import type { CustomerPrivacy } from "./globals";
import {
  AnalyticsEvent,
  type AnalyticsCartLine,
  type AnalyticsEventContext,
  type PageViewPayload,
  type CollectionViewPayload,
  type SearchViewPayload,
  type CartLineUpdatePayload,
  type ProductPayload,
  type ProductPrice,
  type OtherData,
  type ShopAnalytics,
} from "./types";
import { getClientBrowserParameters } from "./utils/browser-params";
import {
  sendShopifyAnalytics,
  MonorailEventName,
  type ShopifyAnalyticsProduct,
  type PageViewPayload as MonorailPageViewPayload,
  type AddToCartPayload as MonorailAddToCartPayload,
} from "./utils/monorail";
import { STOREFRONT_ANALYTICS_VERSION as version } from "./utils/version";

type PayloadWithShop = {
  shop?: Partial<ShopAnalytics> | null;
};

type ValidatedShopAnalytics = ShopAnalytics & { acceptedLanguage: string };

// Products arrive untyped. validateProducts narrows them before any field is read.
type ProductViewInput = PayloadWithShop & { products: unknown[] };

function getCustomerPrivacy(): CustomerPrivacy | null {
  try {
    if (window.Shopify?.customerPrivacy) {
      return window.Shopify.customerPrivacy;
    }
  } catch {}
  return null;
}

function logMissingConfig(fieldName: string) {
  const configPath = fieldName.startsWith("window.") ? fieldName : `shop.${fieldName}`;

  console.error(
    `[shopify:error:analytics] Unable to send Shopify analytics: Missing ${configPath} configuration.`,
  );
}

function warnMissingCurrency(eventName: string) {
  console.warn(
    `[shopify:warn:analytics] ${eventName} was sent without a currency. Publish the variant price as { amount, currencyCode } or set window.Shopify.currency.active, which Hydrogen fills from i18n.currency.`,
  );
}

function hasPriceAmount(price: unknown): boolean {
  if (typeof price === "string") return price !== "";
  return isObjectRecord(price) && typeof price.amount === "string" && price.amount !== "";
}

// Validation proves only the amount, so currencyCode is still checked at runtime.
function readPrice(price: ProductPrice): { amount: string; currencyCode?: string } {
  if (typeof price === "string") return { amount: price };
  const { amount, currencyCode } = price;
  return typeof currencyCode === "string" ? { amount, currencyCode } : { amount };
}

function resolveCurrency(eventCurrency?: string): string | undefined {
  return (eventCurrency || window.Shopify?.currency?.active)?.toUpperCase();
}

function prepareBasePageViewPayload(
  payload: { shop?: Partial<ShopAnalytics> | null },
  context: AnalyticsEventContext,
  eventCurrency?: string,
): MonorailPageViewPayload | undefined {
  const customerPrivacy = getCustomerPrivacy();
  if (!customerPrivacy) return;

  const shop = validateShopAnalytics(payload.shop);
  if (!shop) return;

  const currency = resolveCurrency(eventCurrency);
  const analyticsAllowed = customerPrivacy.analyticsProcessingAllowed();
  const marketingAllowed = customerPrivacy.marketingAllowed();
  const saleOfDataAllowed = customerPrivacy.saleOfDataAllowed();

  return {
    shopifySalesChannel: shop.channel,
    assetVersionId: version,
    ...shop,
    ...(currency ? { currency } : {}),
    hasUserConsent: analyticsAllowed,
    ...getClientBrowserParameters(context.getTrackingValues()),
    analyticsAllowed,
    marketingAllowed,
    saleOfDataAllowed,
    ccpaEnforced: !saleOfDataAllowed,
    gdprEnforced: !(marketingAllowed && analyticsAllowed),
  };
}

function validateShopAnalytics(
  shop: Partial<ShopAnalytics> | null | undefined,
): ValidatedShopAnalytics | undefined {
  if (!shop?.shopId) {
    logMissingConfig("shopId");
    return;
  }

  const acceptedLanguage = window.Shopify?.locale?.toUpperCase();
  if (!acceptedLanguage) {
    logMissingConfig("window.Shopify.locale");
    return;
  }

  if (shop.channel === "hydrogen") {
    if (!shop.storefrontId) {
      logMissingConfig("storefrontId");
      return;
    }

    return {
      shopId: normalizeShopifyShopId(shop.shopId),
      storefrontId: shop.storefrontId,
      channel: shop.channel,
      acceptedLanguage,
    };
  }

  if (shop.channel === "headless") {
    return {
      shopId: normalizeShopifyShopId(shop.shopId),
      channel: shop.channel,
      acceptedLanguage,
    };
  }

  logMissingConfig("channel");
  return;
}

function prepareBaseCartPayload(
  payload: {
    shop?: Partial<ShopAnalytics> | null;
    cart?: { id: string } | null;
  },
  context: AnalyticsEventContext,
  eventCurrency?: string,
): MonorailAddToCartPayload | undefined {
  if (!payload.cart) return;
  const pageViewPayload = prepareBasePageViewPayload(payload, context, eventCurrency);
  if (!pageViewPayload) return;
  return { ...pageViewPayload, cartId: payload.cart.id };
}

function missingErrorMessage(
  type: "cart" | "product",
  fieldName: string,
  isVariantField: boolean,
  viewKeyName?: string,
) {
  if (type === "cart") {
    const name = `${isVariantField ? "merchandise" : "merchandise.product"}.${fieldName}`;
    console.error(
      `[shopify:error:analytics] Can't set up cart analytics events because the \`cart.lines[].${name}\` value is missing from your GraphQL cart query. In your project, search for where \`fragment CartLine on CartLine\` is defined and make sure \`${name}\` is part of your cart query. Check the Hydrogen Skeleton template for reference: https://github.com/Shopify/hydrogen/blob/main/templates/skeleton/app/lib/fragments.ts#L25-L56.`,
    );
  } else {
    const name = viewKeyName || fieldName;
    console.error(
      `[shopify:error:analytics] Can't set up product view analytics events because the \`${name}\` is missing from your \`<Analytics.ProductView>\`. Make sure \`${name}\` is part of your products data prop. Check the Hydrogen Skeleton template for reference: https://github.com/Shopify/hydrogen/blob/main/templates/skeleton/app/routes/products.%24handle.tsx#L159-L165.`,
    );
  }
}

function validateProducts(
  type: "cart" | "product",
  products: unknown[],
): products is Array<ProductPayload & OtherData> {
  if (products.length === 0) {
    missingErrorMessage(type, "", false, "data.products");
    return false;
  }

  for (const product of products) {
    if (!isObjectRecord(product) || !product.id) {
      missingErrorMessage(type, "id", false);
      return false;
    }
    if (!product.title) {
      missingErrorMessage(type, "title", false);
      return false;
    }
    if (!hasPriceAmount(product.price)) {
      missingErrorMessage(type, "price.amount", true, "price");
      return false;
    }
    if (!product.vendor) {
      missingErrorMessage(type, "vendor", false);
      return false;
    }
    if (!product.variantId) {
      missingErrorMessage(type, "id", true, "variantId");
      return false;
    }
    if (!product.variantTitle) {
      missingErrorMessage(type, "title", true, "variantTitle");
      return false;
    }
  }
  return true;
}

function formatProduct(products: Array<ProductPayload & OtherData>): ShopifyAnalyticsProduct[] {
  return products.map((product) => ({
    productGid: product.id,
    variantGid: product.variantId,
    name: product.title,
    variantName: product.variantTitle,
    brand: product.vendor,
    price: readPrice(product.price).amount,
    quantity: product.quantity || 1,
    category: product.productType,
    ...(product.sku ? { sku: product.sku } : {}),
  }));
}

const PAGE_VIEW_SUPPRESSED_TEMPLATES = new Set(["product", "collection", "search"]);

export function createShopifyAnalyticsProcessor() {
  function handlePageView(payload: unknown, context: AnalyticsEventContext) {
    if (!hasPageViewPayload(payload)) return;

    const match = window.Shopify?.routes?.match?.(payload.url ?? window.location.href);
    if (match && PAGE_VIEW_SUPPRESSED_TEMPLATES.has(match.pageTemplateName)) {
      return;
    }

    const eventPayload = prepareBasePageViewPayload(payload, context);
    if (!eventPayload) return;

    sendShopifyAnalytics({
      eventName: MonorailEventName.PAGE_VIEW_2,
      payload: eventPayload,
    });
  }

  function handleProductView(payload: unknown, context: AnalyticsEventContext) {
    if (!hasProductViewPayload(payload)) return;
    if (!validateProducts("product", payload.products)) return;

    const eventCurrency = payload.products
      .map((p) => readPrice(p.price).currencyCode)
      .find(Boolean);
    const eventPayload = prepareBasePageViewPayload(payload, context, eventCurrency);
    if (!eventPayload) return;
    if (!eventPayload.currency) warnMissingCurrency(AnalyticsEvent.PRODUCT_VIEWED);

    const formattedProducts = formatProduct(payload.products);

    sendShopifyAnalytics({
      eventName: MonorailEventName.PRODUCT_VIEW,
      payload: { ...eventPayload, products: formattedProducts },
    });
  }

  function handleCollectionView(payload: unknown, context: AnalyticsEventContext) {
    if (!hasCollectionViewPayload(payload)) return;

    const eventPayload = prepareBasePageViewPayload(payload, context);
    if (!eventPayload) return;

    sendShopifyAnalytics({
      eventName: MonorailEventName.COLLECTION_VIEW,
      payload: {
        ...eventPayload,
        collectionHandle: payload.collection.handle,
        collectionId: payload.collection.id,
      },
    });
  }

  function handleSearchView(payload: unknown, context: AnalyticsEventContext) {
    if (!hasSearchViewPayload(payload)) return;

    const eventPayload = prepareBasePageViewPayload(payload, context);
    if (!eventPayload) return;

    sendShopifyAnalytics({
      eventName: MonorailEventName.SEARCH_VIEW,
      payload: { ...eventPayload, searchString: payload.searchTerm },
    });
  }

  function handleProductAddedToCart(payload: unknown, context: AnalyticsEventContext) {
    if (!hasCartLineUpdatePayload(payload)) return;

    const { cart, currentLine } = payload;
    const eventCurrency = currentLine && readPrice(currentLine.merchandise.price).currencyCode;
    const eventPayload = prepareBaseCartPayload(
      { shop: payload.shop, cart },
      context,
      eventCurrency,
    );
    if (!eventPayload || !currentLine?.id) return;

    const product = {
      id: currentLine.merchandise.product.id,
      variantId: currentLine.merchandise.id,
      title: currentLine.merchandise.product.title,
      variantTitle: currentLine.merchandise.title,
      vendor: currentLine.merchandise.product.vendor,
      price: currentLine.merchandise.price.amount,
      quantity: currentLine.quantity,
      productType: currentLine.merchandise.product.productType,
      sku: currentLine.merchandise.sku,
    };

    const products = [product];
    if (!validateProducts("cart", products)) return;
    if (!eventPayload.currency) warnMissingCurrency(AnalyticsEvent.PRODUCT_ADD_TO_CART);

    sendShopifyAnalytics({
      eventName: MonorailEventName.ADD_TO_CART,
      payload: { ...eventPayload, products: formatProduct(products) },
    });
  }

  return {
    handleEvent(event: string, payload: unknown, context: AnalyticsEventContext) {
      if (event === AnalyticsEvent.PAGE_VIEWED) handlePageView(payload, context);
      else if (event === AnalyticsEvent.PRODUCT_VIEWED) handleProductView(payload, context);
      else if (event === AnalyticsEvent.COLLECTION_VIEWED) handleCollectionView(payload, context);
      else if (event === AnalyticsEvent.SEARCH_VIEWED) handleSearchView(payload, context);
      else if (event === AnalyticsEvent.PRODUCT_ADD_TO_CART)
        handleProductAddedToCart(payload, context);
    },
  };
}

function hasPageViewPayload(payload: unknown): payload is PageViewPayload {
  return hasOptionalShop(payload);
}

function hasProductViewPayload(payload: unknown): payload is ProductViewInput {
  return hasOptionalShop(payload) && Array.isArray(payload.products);
}

function hasCollectionViewPayload(payload: unknown): payload is CollectionViewPayload {
  return (
    hasOptionalShop(payload) &&
    isObjectRecord(payload.collection) &&
    typeof payload.collection.id === "string" &&
    typeof payload.collection.handle === "string"
  );
}

function hasSearchViewPayload(payload: unknown): payload is SearchViewPayload {
  return hasOptionalShop(payload) && typeof payload.searchTerm === "string";
}

function hasCartLineUpdatePayload(payload: unknown): payload is CartLineUpdatePayload {
  return (
    hasOptionalShop(payload) &&
    hasNullableCart(payload.cart) &&
    (!("currentLine" in payload) || hasAnalyticsCartLine(payload.currentLine))
  );
}

function hasOptionalShop(payload: unknown): payload is PayloadWithShop & Record<string, unknown> {
  if (!isObjectRecord(payload)) return false;
  if (!("shop" in payload)) return true;

  return payload.shop === null || isObjectRecord(payload.shop);
}

function hasNullableCart(cart: unknown): cart is { id: string } | null {
  return cart === null || (isObjectRecord(cart) && typeof cart.id === "string");
}

function hasAnalyticsCartLine(line: unknown): line is AnalyticsCartLine {
  if (!isObjectRecord(line) || typeof line.id !== "string") return false;
  if (typeof line.quantity !== "number" || !isObjectRecord(line.merchandise)) return false;

  const { merchandise } = line;
  const { price, product } = merchandise;
  if (!isObjectRecord(price) || !isObjectRecord(product)) return false;

  return (
    typeof merchandise.id === "string" &&
    typeof merchandise.title === "string" &&
    typeof price.amount === "string" &&
    typeof product.id === "string" &&
    typeof product.title === "string" &&
    typeof product.vendor === "string"
  );
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
